import { ArrowRight, FolderOpen, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ProjectPickerList } from "@/components/building/ProjectPickerList";
import { useProjectsActivity } from "@/domains/documents/hooks/useProjectsActivity";
import { ProjectActivityLine } from "@/domains/projects/components/ProjectActivityLine";
import { useCreateProjectDialog } from "@/domains/projects/components/createProjectDialogContext";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { orderActiveFirst } from "@/domains/projects/status/projectStatus";
import {
  PROJECT_TILE_CLASS,
  PROJECT_TILE_INTERACTIVE_CLASS,
  PROJECT_TILE_TEXT_INDENT_CLASS,
} from "@/domains/projects/components/projectTile";
import { usePermissions } from "@/hooks/usePermissions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import type { BuildingScopeProjectOption } from "@/hooks/buildings/useBuildingScope";

/** Up to a 3×3 grid of tiles; past that a searchable list scales better. */
export const PROJECT_CHOOSER_MAX_TILES = 9;

/** Grid width follows the tile count, so one or two tiles sit centred rather
 *  than left-aligned in a three-column track with empty slots beside them. */
const GRID_LAYOUT_BY_COUNT: Record<number, string> = {
  1: "max-w-[15rem] grid-cols-1",
  2: "max-w-lg grid-cols-1 sm:grid-cols-2",
};
const GRID_LAYOUT_DEFAULT = "max-w-3xl grid-cols-1 sm:grid-cols-2 md:grid-cols-3";

interface ProjectScopeChooserProps {
  buildingId: string;
  projects: BuildingScopeProjectOption[];
  onSelect: (projectId: number) => void;
}

/**
 * The "choose a project" screen BuildingLayout shows when a projects-view
 * route has no resolved scope — a deep link, or a remembered project that has
 * gone. Centred in the content column: a few projects are tiles carrying their
 * recent activity, many are a searchable list in a card. Tiles follow the
 * picker's status rules — active projects first, a status dot before each
 * name — and the list brings its own (dots, plus Active / Inactive tabs). "New
 * project" comes from the layout's shared dialog, which also scopes to what it
 * creates.
 */
export function ProjectScopeChooser({ buildingId, projects, onSelect }: ProjectScopeChooserProps) {
  const { t } = useLanguage();
  const { openCreateProject, canCreateProjects } = useCreateProjectDialog();
  const showTiles = projects.length <= PROJECT_CHOOSER_MAX_TILES;

  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-6 p-6"
      data-testid="project-scope-chooser"
    >
      <div className="flex flex-col items-center gap-1 text-center">
        <h2 className="text-xl font-semibold">{t.projects.chooser.title}</h2>
        <p className="text-sm text-muted-foreground">{t.projects.chooser.subtitle}</p>
      </div>

      {showTiles ? (
        <>
          <ProjectTileGrid buildingId={buildingId} projects={projects} onSelect={onSelect} />
          {canCreateProjects && (
            <Button variant="ghost" size="sm" onClick={openCreateProject}>
              <Plus className="mr-1.5 h-4 w-4" />
              {t.projects.selector.newProject}
            </Button>
          )}
        </>
      ) : (
        <div className="w-full max-w-sm overflow-hidden rounded-[12px] border border-black/[0.06] bg-white text-left shadow-lift">
          <ProjectPickerList
            projects={projects}
            onSelect={onSelect}
            onCreateProject={canCreateProjects ? openCreateProject : undefined}
          />
        </div>
      )}
    </div>
  );
}

function ProjectTileGrid({ buildingId, projects, onSelect }: ProjectScopeChooserProps) {
  const { canViewPMTool } = usePermissions();
  const pmModuleEnabled = useFeatureFlag(FEATURE_FLAGS.PM_MODULE_ENABLED, true);
  const {
    data: activity,
    isSuccess,
    isError,
  } = useProjectsActivity(buildingId, {
    includeIssues: pmModuleEnabled && canViewPMTool,
  });

  return (
    <ul
      className={cn(
        "grid w-full gap-3",
        GRID_LAYOUT_BY_COUNT[projects.length] ?? GRID_LAYOUT_DEFAULT
      )}
    >
      {orderActiveFirst(projects).map((project) => (
        <li key={project.id}>
          <button
            type="button"
            onClick={() => onSelect(project.id)}
            className={cn(
              PROJECT_TILE_CLASS,
              PROJECT_TILE_INTERACTIVE_CLASS,
              "group h-full min-h-[96px] w-full gap-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            )}
          >
            <span className="flex items-start gap-2">
              <FolderOpen className="h-5 w-5 shrink-0 text-primary" />
              {/* h-2 dot centred on the h-5 title line. */}
              <ProjectStatusBadge variant="dot" status={project.status} className="mt-1.5" />
              <span className="line-clamp-2 flex-1 text-sm font-semibold leading-5">
                {project.name}
              </span>
              <ArrowRight
                aria-hidden
                className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              />
            </span>
            {/* Nothing until the counts land, so a busy project never flashes
                "No recent activity" first. */}
            {(isSuccess || isError) && (
              <ProjectActivityLine
                activity={activity?.get(project.id)}
                isError={isError}
                className={cn("mt-auto", PROJECT_TILE_TEXT_INDENT_CLASS)}
              />
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
